import { useEffect, useState } from 'react';

import {
  isOwnMessage,
  MessagingChannel,
  MessagingChannelMember,
  MessagingMessage,
} from '~hooks/useMessaging/models';
import { Nullable } from '~types/Nullable';

// Five pages is as much history as one open pulls in; past that the newest message is a fine anchor.
const UNREAD_DIVIDER_PAGE_CAP = 5;

type ReadPositionAtOpen =
  | { hasUnread: false }
  | { hasUnread: true; viewer: MessagingChannelMember };

type UnreadDividerState = {
  channelId: Nullable<string>;
  firstMessageIdAtLastRequest?: string;
  hasRequestedPageStartedLoading: boolean;
  pagesRequested: number;
  readPositionAtOpen?: ReadPositionAtOpen;
  // Undefined while the first unread message may still be on an unloaded page.
  unreadDividerMessageId?: Nullable<string>;
};

const stateForChannel = (channelId: Nullable<string>): UnreadDividerState => ({
  channelId,
  hasRequestedPageStartedLoading: false,
  pagesRequested: 0,
});

const readPositionOf = (
  channel: MessagingChannel,
  viewerUserId: string,
): ReadPositionAtOpen => {
  const viewer = channel.memberFor(viewerUserId);
  if (!viewer || channel.unreadCount === 0) return { hasUnread: false };

  return { hasUnread: true, viewer };
};

const firstUnreadMessageId = (
  messages: MessagingMessage[],
  viewer: MessagingChannelMember,
  viewerUserId: Nullable<string>,
) => {
  const firstUnread = messages.find(
    (message) =>
      !message.isDeleted &&
      !isOwnMessage(message, viewerUserId) &&
      !viewer.hasRead(message),
  );

  return firstUnread?.id ?? null;
};

export default function useUnreadDivider({
  channel,
  channelId,
  hasLoadFailed,
  hasOlder,
  hasOlderLoadFailed,
  isLoading,
  isLoadingOlder,
  isRefreshing,
  messages,
  onLoadOlder,
  viewerUserId,
}: {
  channel: Nullable<MessagingChannel>;
  channelId: Nullable<string>;
  hasLoadFailed: boolean;
  hasOlder: boolean;
  hasOlderLoadFailed: boolean;
  isLoading: boolean;
  isLoadingOlder: boolean;
  isRefreshing: boolean;
  messages: MessagingMessage[];
  onLoadOlder: () => void;
  viewerUserId: Nullable<string>;
}) {
  const [storedState, setState] = useState(() => stateForChannel(channelId));
  const isStateForAnotherChannel = storedState.channelId !== channelId;
  if (isStateForAnotherChannel) {
    setState(stateForChannel(channelId));
  }
  const state = isStateForAnotherChannel ? stateForChannel(channelId) : storedState;
  const updateState = (changes: Partial<UnreadDividerState>) =>
    setState((previous) => ({ ...previous, ...changes }));

  // Captured once per open from a settled channel, before mark-read moves the viewer's mark under it.
  const isReadPositionPending = !state.readPositionAtOpen && !isRefreshing;
  if (isReadPositionPending && channel?.id === channelId && channel && viewerUserId) {
    updateState({ readPositionAtOpen: readPositionOf(channel, viewerUserId) });
  }

  const oldestLoadedMessage = messages.at(0);
  const firstMessageId = oldestLoadedMessage?.id;
  const isAwaitingRequestedPage =
    state.pagesRequested > 0 &&
    hasOlder &&
    !hasOlderLoadFailed &&
    firstMessageId === state.firstMessageIdAtLastRequest;
  if (
    isAwaitingRequestedPage &&
    isLoadingOlder &&
    !state.hasRequestedPageStartedLoading
  ) {
    updateState({ hasRequestedPageStartedLoading: true });
  }
  // A request that stops loading without bringing a page in was cancelled, as a cache invalidation does.
  const wasRequestedPageDropped = state.hasRequestedPageStartedLoading && !isLoadingOlder;
  const isRequestedPageInFlight = isAwaitingRequestedPage && !wasRequestedPageDropped;

  // A cached transcript can predate the unread messages until its refetch on open lands.
  const isResolvingUnreadDivider =
    state.unreadDividerMessageId === undefined &&
    Boolean(state.readPositionAtOpen) &&
    !isLoading &&
    !isRefreshing;
  const viewerAtOpen = state.readPositionAtOpen?.hasUnread
    ? state.readPositionAtOpen.viewer
    : null;
  const isOldestLoadedMessageRead = Boolean(
    oldestLoadedMessage && viewerAtOpen?.hasRead(oldestLoadedMessage),
  );
  const isUnreadBoundaryLoaded = !hasOlder || isOldestLoadedMessageRead;
  const hasReachedPageCap = state.pagesRequested >= UNREAD_DIVIDER_PAGE_CAP;

  // A failed page stays unresolved rather than final, so a successful retry still places the divider.
  if (isResolvingUnreadDivider && !hasLoadFailed) {
    if (!viewerAtOpen) {
      updateState({ unreadDividerMessageId: null });
    } else if (isUnreadBoundaryLoaded) {
      updateState({
        unreadDividerMessageId: firstUnreadMessageId(
          messages,
          viewerAtOpen,
          viewerUserId,
        ),
      });
    } else if (hasReachedPageCap && !isRequestedPageInFlight) {
      updateState({ unreadDividerMessageId: null });
    }
  }

  const shouldFetchOlderPage =
    isResolvingUnreadDivider &&
    Boolean(viewerAtOpen) &&
    !hasLoadFailed &&
    !isUnreadBoundaryLoaded &&
    !hasOlderLoadFailed &&
    !hasReachedPageCap &&
    !isRequestedPageInFlight &&
    !isLoadingOlder;

  useEffect(() => {
    if (!shouldFetchOlderPage) return;

    setState((previous) => ({
      ...previous,
      firstMessageIdAtLastRequest: firstMessageId,
      hasRequestedPageStartedLoading: false,
      pagesRequested: previous.pagesRequested + 1,
    }));
    onLoadOlder();
  }, [firstMessageId, onLoadOlder, shouldFetchOlderPage]);

  return { unreadDividerMessageId: state.unreadDividerMessageId ?? null };
}
